import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Save, Copy, BookMarked, Wand2, ChevronDown } from "lucide-react";

/**
 * Reusable writing surface: AI auto-generate, edit yourself, save & reuse templates.
 * props: value={subject, body}, onSave(value), category, aiContext, compact
 */
export const CopyStudio = ({ value, onSave, category = "general", aiContext = "", subjectLabel = "Subject" }) => {
  const [subject, setSubject] = useState(value?.subject || "");
  const [body, setBody] = useState(value?.body || "");
  const [instruction, setInstruction] = useState("");
  const [writing, setWriting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setSubject(value?.subject || ""); setBody(value?.body || ""); setDirty(false); }, [value?.subject, value?.body]);
  const loadTpl = () => api.getTemplates(category).then(setTemplates).catch(() => {});
  useEffect(() => { loadTpl(); }, []); // eslint-disable-line

  const writeAI = async () => {
    setWriting(true);
    try {
      const r = await api.writeCopy(instruction || "Improve and rewrite this copy to convert better.", `${aiContext}\n\nCurrent draft: ${body}`, "");
      if (r.subject) setSubject(r.subject);
      setBody(r.body || body); setDirty(true);
      toast.success("KURT wrote a fresh draft — edit as you like.");
    } catch { toast.error("Couldn't write copy."); }
    finally { setWriting(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await onSave({ subject, body }); setDirty(false); toast.success("Saved."); }
    catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name:");
    if (!name) return;
    try { await api.addTemplate({ name, category, subject, body }); await loadTpl(); toast.success("Template saved."); }
    catch { toast.error("Couldn't save template."); }
  };

  const applyTemplate = (t) => { setSubject(t.subject || ""); setBody(t.body || ""); setDirty(true); setShowTpl(false); };
  const removeTemplate = async (id, e) => { e.stopPropagation(); await api.deleteTemplate(id); loadTpl(); };
  const copy = () => { navigator.clipboard?.writeText([subject, body].filter(Boolean).join("\n\n")); toast.success("Copied."); };

  return (
    <div className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3 space-y-2" data-testid="copy-studio">
      {typeof value?.subject !== "undefined" && (
        <input data-testid="copy-subject" value={subject} onChange={(e) => { setSubject(e.target.value); setDirty(true); }} placeholder={subjectLabel}
          className="w-full bg-[#121824] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-200 outline-none focus:border-emerald-500/40" />
      )}
      <textarea data-testid="copy-body" value={body} onChange={(e) => { setBody(e.target.value); setDirty(true); }} rows={5}
        className="w-full bg-[#121824] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-slate-300 outline-none focus:border-emerald-500/40 resize-y leading-relaxed" />
      <div className="flex items-center gap-1.5">
        <input data-testid="copy-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !writing && writeAI()}
          placeholder="Tell KURT what to write / tweak…"
          className="flex-1 bg-[#121824] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-emerald-500/40" />
        <button data-testid="copy-write-ai" onClick={writeAI} disabled={writing} title="Write with AI"
          className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-500/90 text-[#04140d] hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {writing ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} AI
        </button>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {onSave && (
          <button data-testid="copy-save" onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] disabled:opacity-50 transition-all">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        )}
        <button data-testid="copy-save-template" onClick={saveTemplate} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all"><BookMarked size={12} /> Save template</button>
        <div className="relative">
          <button data-testid="copy-load-template" onClick={() => { loadTpl(); setShowTpl(!showTpl); }} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all">Templates <ChevronDown size={12} /></button>
          {showTpl && (
            <div className="absolute z-20 mt-1 w-64 max-h-56 overflow-auto rounded-lg border border-white/10 bg-[#121824] p-1 shadow-xl">
              {templates.length === 0 ? <p className="text-xs text-slate-500 px-2 py-1.5">No templates yet.</p> :
                templates.map((t) => (
                  <div key={t.id} onClick={() => applyTemplate(t)} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-white/[0.06] cursor-pointer">
                    <span className="text-xs text-slate-200 truncate">{t.name}</span>
                    <button onClick={(e) => removeTemplate(t.id, e)} className="text-[10px] text-red-400 hover:text-red-300">✕</button>
                  </div>
                ))}
            </div>
          )}
        </div>
        <button data-testid="copy-copy" onClick={copy} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all"><Copy size={12} /> Copy</button>
      </div>
    </div>
  );
};
